<script setup lang="ts">
import { computed, watch, onMounted, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import { InformationCircleIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { useHelpDrawerStore } from '@/stores/useHelpDrawerStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import type { HelpVariant } from '@/help/registry'
import MarkdownContent from '@/components/common/layout/MarkdownContent.vue'

// Panneau d'aide global. Déclenché par <HelpButton> placé à côté des titres.
// - Variante `user` : visible par tous.
// - Variante `dev` : réservée aux USER_MASTER, accessible via le toggle.
const helpStore = useHelpDrawerStore()
const permissionStore = useBoostCorePermissionStore()
const { isOpen, variant, hasUser, hasDev } = storeToRefs(helpStore)

const isUserMaster = computed(() => permissionStore.include('USER_MASTER'))

/** Variantes accessibles à l'utilisateur courant (dev en premier). */
const allowedVariants = computed<HelpVariant[]>(() => {
  if (isUserMaster.value) {
    const vs: HelpVariant[] = []
    if (hasDev.value) vs.push('dev')
    if (hasUser.value) vs.push('user')
    return vs
  }
  // Utilisateur normal : uniquement la doc utilisateur.
  return hasUser.value ? ['user'] : []
})

/** Le panneau ne peut s'afficher que si l'utilisateur a accès à une variante. */
const canSeeHelp = computed(() => allowedVariants.value.length > 0)

/** Toggle visible seulement si les deux variantes sont accessibles. */
const showTabs = computed(
  () => isUserMaster.value && hasDev.value && hasUser.value,
)

/** Variante réellement affichée (clampée aux variantes autorisées). */
const effectiveVariant = computed<HelpVariant>(() =>
  allowedVariants.value.includes(variant.value)
    ? variant.value
    : allowedVariants.value[0] ?? 'user',
)

const content = computed(() => helpStore.contentFor(effectiveVariant.value))

function selectVariant(v: HelpVariant) {
  helpStore.setVariant(v)
}

function close() {
  helpStore.close()
}

// Fermeture au clavier (Échap).
function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && isOpen.value) close()
}
onMounted(() => document.addEventListener('keydown', onKeydown))
onUnmounted(() => document.removeEventListener('keydown', onKeydown))

// Si le contexte perd toute aide accessible, referme le panneau.
watch(canSeeHelp, (ok) => {
  if (!ok) close()
})
</script>

<template>
  <!--
    Sidebar non bloquante : pas d'overlay plein écran, la page (et les modales)
    restent interactives pendant la lecture de l'aide. z-index au-dessus des
    modales (≈ z-1100). Fermeture via la croix ou la touche Échap.
  -->
  <Transition name="help-slide">
    <aside
      v-if="isOpen && canSeeHelp"
      class="fixed right-0 top-0 z-1300 flex h-full w-full max-w-md flex-col border-l border-base-300 bg-base-100 shadow-2xl"
      role="dialog"
      aria-label="Aide"
    >
      <header class="flex items-center justify-between border-b border-base-300 p-4">
        <h2 class="flex items-center gap-2 text-lg font-bold">
          <InformationCircleIcon class="h-5 w-5 text-primary" aria-hidden="true" />
          Aide
        </h2>
        <button
          type="button"
          class="btn btn-ghost btn-sm btn-circle"
          aria-label="Fermer"
          @click="close"
        >
          <XMarkIcon class="h-5 w-5" aria-hidden="true" />
        </button>
      </header>

      <!-- Toggle Développeur / Utilisateur (USER_MASTER, si les deux existent) -->
      <div v-if="showTabs" role="tablist" class="tabs tabs-boxed m-3 mb-0">
        <button
          type="button"
          role="tab"
          class="tab"
          :class="{ 'tab-active': effectiveVariant === 'dev' }"
          @click="selectVariant('dev')"
        >
          Développeur
        </button>
        <button
          type="button"
          role="tab"
          class="tab"
          :class="{ 'tab-active': effectiveVariant === 'user' }"
          @click="selectVariant('user')"
        >
          Utilisateur
        </button>
      </div>

      <div class="flex-1 overflow-y-auto p-4">
        <MarkdownContent v-if="content" :content="content" />
      </div>
    </aside>
  </Transition>
</template>

<style scoped>
.help-slide-enter-active,
.help-slide-leave-active {
  transition: transform 0.25s ease;
}
.help-slide-enter-from,
.help-slide-leave-to {
  transform: translateX(100%);
}
</style>
